import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Download, ShieldAlert } from "lucide-react";
import { jsPDF } from "jspdf";
import { useLanguage, tr } from "../i18n";
import { lp } from "../components/Navbar";
import { Kicker, Reveal } from "../components/motion";
import { GENERAL_QUESTIONS, ERI_SECTIONS, computeResults } from "../data/eriQuestions";
import { useAuth, api } from "../auth/AuthContext";
const STEPS = ["General", ...ERI_SECTIONS.map(s => s.title), "Details", "Result"];
const bandStyles = {
  red: "border-red-600/30 bg-red-50 text-red-700",
  amber: "border-amber-600/30 bg-amber-50 text-amber-700",
  green: "border-emerald-600/30 bg-emerald-50 text-emerald-700"
};
const barStyles = {
  red: "bg-red-500",
  amber: "bg-amber-500",
  green: "bg-emerald-600"
};
function OptionRow({
  selected,
  onSelect,
  children,
  testid
}) {
  return <button type="button" onClick={onSelect} data-testid={testid} className={`flex w-full items-center gap-3 border px-4 py-3 text-left text-sm transition-all ${selected ? "border-esaia-blue bg-esaia-blue/5 font-medium text-esaia-ink" : "border-esaia-line bg-white text-esaia-muted hover:border-esaia-blue/50 hover:text-esaia-ink"}`}>
      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${selected ? "border-esaia-blue bg-esaia-blue text-white" : "border-esaia-line"}`}>
        {selected && <Check size={10} />}
      </span>
      {children}
    </button>;
}
export default function EriAssessment() {
  const {
    lang
  } = useLanguage();
  const {
    user
  } = useAuth();
  const [step, setStep] = useState(0);
  const [general, setGeneral] = useState({});
  const [scored, setScored] = useState({});
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [disclaimer, setDisclaimer] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (user) {
      setEmail(v => v || user.email || "");
      setCompany(v => v || user.company || "");
      setFirstName(v => v || user.first_name || "");
      setLastName(v => v || user.last_name || "");
    }
  }, [user]);
  const results = useMemo(() => computeResults(scored), [scored]);
  const totalSteps = ERI_SECTIONS.length + 2; // general + sections + details
  const onResult = step >= totalSteps;
  const stepValid = () => {
    if (step === 0) return GENERAL_QUESTIONS.every(q => general[q.id]);
    if (step <= ERI_SECTIONS.length) {
      const section = ERI_SECTIONS[step - 1];
      return section.questions.every(q => scored[q.n] !== undefined);
    }
    return disclaimer;
  };
  const downloadPdf = () => {
    const doc = new jsPDF({
      unit: "pt",
      format: "a4"
    });
    const M = 56;
    let y = 70;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(15);
    doc.setTextColor(43, 92, 184);
    doc.text("E S A I A", M, y);
    y += 15;
    doc.setFont("times", "italic");
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(tr("Transparent. Accountable. Ethical."), M, y);
    y += 34;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(15, 23, 42);
    doc.text("Ethical Readiness Index", M, y);
    y += 20;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(71, 85, 105);
    const nameLine = [`${firstName} ${lastName}`.trim(), company, email].filter(Boolean).join("   ·   ");
    if (nameLine) {
      doc.text(nameLine, M, y);
      y += 15;
    }
    doc.text(`${tr("Completed")}: ${new Date().toLocaleDateString("en-GB")}`, M, y);
    y += 40;
    doc.setFont("times", "normal");
    doc.setFontSize(44);
    doc.setTextColor(43, 92, 184);
    doc.text(`${results.total}`, M, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(100, 116, 139);
    doc.text("/100", M + 62, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(15, 23, 42);
    doc.text(`${tr("Readiness level")}: ${tr(results.band.label)}`, M + 130, y);
    y += 34;
    doc.setDrawColor(226, 232, 240);
    doc.setFillColor(226, 232, 240);
    results.sectionScores.forEach(s => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(15, 23, 42);
      doc.text(tr(s.title), M, y);
      doc.text(`${s.pct}%`, M + 320, y);
      doc.rect(M, y + 7, 320, 5, "F");
      doc.setFillColor(43, 92, 184);
      doc.rect(M, y + 7, 3.2 * s.pct, 5, "F");
      doc.setFillColor(226, 232, 240);
      y += 28;
    });
    if (results.attention.length > 0 && y < 700) {
      y += 10;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.text(tr("Key areas requiring attention"), M, y);
      y += 17;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      for (const a of results.attention.slice(0, 8)) {
        const lines = doc.splitTextToSize(`${tr(a.section)}: ${tr(a.text)}`, 483);
        if (y + lines.length * 12 > 740) break;
        doc.text(lines, M, y);
        y += lines.length * 12 + 5;
      }
    }
    doc.setFontSize(7.5);
    doc.setTextColor(148, 163, 184);
    doc.text(doc.splitTextToSize(tr("The Ethical Readiness Index is an introductory, non-binding readiness indication. It does not constitute an audit, certification or legal advice. ESAIA | Ethical Standards for Artificial Intelligence Assurance · info@esaia.org"), 483), M, 780);
    doc.save("ESAIA-ERI-Result.pdf");
  };
  const next = async () => {
    setError("");
    if (step < ERI_SECTIONS.length) {
      setStep(step + 1);
      window.scrollTo({
        top: 0
      });
      return;
    }
    setSubmitting(true);
    try {
      const detailed = ERI_SECTIONS.flatMap(s => s.questions.map(q => ({
        n: q.n,
        section: s.title,
        question: q.text,
        answer: q.options[2 - (scored[q.n] ?? 0)]
      })));
      const payload = {
        general,
        answers: scored,
        detailed_answers: detailed,
        attention: results.attention,
        section_scores: Object.fromEntries(results.sectionScores.map(s => [s.title, s.pct])),
        total_score: results.total,
        band: results.band.label,
        company,
        first_name: firstName,
        last_name: lastName,
        lang
      };
      if (email) payload.email = email;
      await api.post("/eri", payload);
      setStep(step + 1);
      window.scrollTo({
        top: 0
      });
    } catch {
      setError(tr("Something went wrong while saving your result. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };
  const back = () => {
    setError("");
    setStep(Math.max(0, step - 1));
    window.scrollTo({
      top: 0
    });
  };
  const progress = Math.min(100, Math.round(step / (totalSteps - 1) * 100));
  return <div data-testid="eri-assessment-page">
      <section className="border-b border-esaia-line bg-white hairline-grid">
        <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6 sm:py-16">
          <Kicker>{tr("Ethical Readiness Index · Free")}</Kicker>
          <h1 className="mt-4 text-3xl font-semibold tracking-tight text-esaia-ink sm:text-4xl">
            {onResult ? tr("Your ERI result") : tr("How ready is your organization for responsible AI?")}
          </h1>
          {!onResult && <div className="mt-8">
              <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle">
                <span data-testid="eri-step-label">{tr("Step")}{" "}{Math.min(step + 1, totalSteps)}{" "}{tr("of")}{" "}{totalSteps} · {tr(STEPS[step])}
                </span>
                <span>{progress}%</span>
              </div>
              <div className="mt-2 h-1 w-full bg-esaia-line">
                <div className="h-1 bg-esaia-blue transition-all duration-500" style={{
              width: `${progress}%`
            }} />
              </div>
            </div>}
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.35,
          ease: [0.22, 1, 0.36, 1]
        }}>
            {/* STEP 0: GENERAL */}
            {step === 0 && <div className="space-y-8" data-testid="eri-assessment-form">
                <p className="text-sm leading-relaxed text-esaia-muted">{tr("General, non-personal information about your organization. This helps ESAIA contextualize your readiness score.")}{" "}</p>
                {GENERAL_QUESTIONS.map(q => <div key={q.id}>
                    <p className="text-sm font-semibold text-esaia-ink">
                      <span className="mr-2 font-mono text-xs text-esaia-blue">{q.id}</span>
                      {tr(q.label)}
                    </p>
                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {q.options.map(o => <OptionRow key={o} selected={general[q.id] === o} onSelect={() => setGeneral(g => ({
                  ...g,
                  [q.id]: o
                }))} testid={`eri-option-${q.id}`}>
                          {tr(o)}
                        </OptionRow>)}
                    </div>
                  </div>)}
              </div>}

            {/* STEPS 1-5: SCORED SECTIONS */}
            {step >= 1 && step <= ERI_SECTIONS.length && <div className="space-y-8">
                {(() => {
              const section = ERI_SECTIONS[step - 1];
              return section.questions.map(q => <div key={q.n}>
                      <p className="text-sm font-semibold text-esaia-ink">
                        <span className="mr-2 font-mono text-xs text-esaia-blue">{q.n}.</span>
                        {tr(q.text)}
                      </p>
                      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
                        {q.options.map((o, oi) => <OptionRow key={o} selected={scored[q.n] === 2 - oi} onSelect={() => setScored(s => ({
                    ...s,
                    [q.n]: 2 - oi
                  }))} testid={`eri-question-option-${q.n}-${oi}`}>
                            {tr(o)}
                          </OptionRow>)}
                      </div>
                    </div>);
            })()}
              </div>}

            {/* STEP 6: DETAILS + DISCLAIMER */}
            {step === ERI_SECTIONS.length + 1 && <div className="space-y-8">
                <div>
                  <p className="text-sm font-semibold text-esaia-ink">{tr("Your details (optional)")}</p>
                  <p className="mt-1 text-xs text-esaia-subtle">{tr("Leave your work email if you would like ESAIA to follow up on your result.")}{" "}</p>
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder={tr("First name")} data-testid="eri-detail-first-name" className="w-full border border-esaia-line bg-white px-3 py-2.5 text-sm text-esaia-ink placeholder:text-esaia-subtle focus:border-esaia-blue focus:outline-none" />
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder={tr("Last name")} data-testid="eri-detail-last-name" className="w-full border border-esaia-line bg-white px-3 py-2.5 text-sm text-esaia-ink placeholder:text-esaia-subtle focus:border-esaia-blue focus:outline-none" />
                    <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder={tr("name@company.com")} data-testid="eri-detail-email" className="w-full border border-esaia-line bg-white px-3 py-2.5 text-sm text-esaia-ink placeholder:text-esaia-subtle focus:border-esaia-blue focus:outline-none" />
                    <input type="text" value={company} onChange={e => setCompany(e.target.value)} placeholder={tr("Organization name")} data-testid="eri-detail-company" className="w-full border border-esaia-line bg-white px-3 py-2.5 text-sm text-esaia-ink placeholder:text-esaia-subtle focus:border-esaia-blue focus:outline-none" />
                  </div>
                </div>
                <button type="button" onClick={() => setDisclaimer(!disclaimer)} data-testid="eri-disclaimer-checkbox" className={`flex w-full items-start gap-3 border p-5 text-left transition-colors ${disclaimer ? "border-esaia-blue bg-esaia-blue/5" : "border-esaia-line bg-white"}`}>
                  <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border ${disclaimer ? "border-esaia-blue bg-esaia-blue text-white" : "border-esaia-line"}`}>
                    {disclaimer && <Check size={12} />}
                  </span>
                  <span className="text-sm leading-relaxed text-esaia-muted">{tr("I have taken note of the disclaimer and understand that this survey does not constitute an audit, certification or legal advice.")}{" "}</span>
                </button>
                {error && <p className="flex items-center gap-2 text-sm text-red-600" data-testid="eri-error">
                    <ShieldAlert size={15} /> {error}
                  </p>}
              </div>}

            {/* RESULT */}
            {onResult && <div data-testid="eri-result">
                <Reveal>
                  <div className="border border-esaia-line bg-white p-8 text-center sm:p-12">
                    <Kicker>{tr("Ethical Readiness Index")}</Kicker>
                    <p className="mt-6 font-serif text-7xl font-light text-esaia-blue" data-testid="eri-result-score">
                      {results.total}
                    </p>
                    <p className="mt-1 font-mono text-xs uppercase tracking-[0.25em] text-esaia-subtle">{tr("out of 100")}</p>
                    <span className={`mt-6 inline-block border px-5 py-2 font-mono text-xs uppercase tracking-[0.2em] ${bandStyles[results.band.tone]}`} data-testid="eri-result-band">
                      {tr(results.band.label)}
                    </span>
                  </div>
                </Reveal>

                <Reveal delay={0.15} className="mt-8 border border-esaia-line bg-white p-8">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle">{tr("Score per area")}{" "}</p>
                  <div className="mt-5 space-y-5">
                    {results.sectionScores.map(s => {
                  const tone = s.pct >= 70 ? "green" : s.pct >= 40 ? "amber" : "red";
                  return <div key={s.id} data-testid={`eri-section-score-${s.id}`}>
                          <div className="flex items-center justify-between text-sm">
                            <span className="font-medium text-esaia-ink">{tr(s.title)}</span>
                            <span className="font-mono text-xs text-esaia-subtle">{s.pct}%</span>
                          </div>
                          <div className="mt-2 h-2 w-full bg-esaia-line">
                            <motion.div className={`h-2 ${barStyles[tone]}`} initial={{
                        width: 0
                      }} animate={{
                        width: `${s.pct}%`
                      }} transition={{
                        duration: 0.9,
                        ease: [0.22, 1, 0.36, 1]
                      }} />
                          </div>
                        </div>;
                })}
                  </div>
                </Reveal>

                {results.attention.length > 0 && <Reveal delay={0.25} className="mt-8 border border-esaia-line bg-white p-8">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-esaia-subtle">{tr("Key areas requiring attention")}{" "}</p>
                    <ul className="mt-5 space-y-3">
                      {results.attention.slice(0, 6).map((a, i) => <li key={i} className="flex items-start gap-3 text-sm text-esaia-muted" data-testid={`eri-attention-${i}`}>
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-red-500" />
                          <span>
                            <span className="font-medium text-esaia-ink">{tr(a.section)}:</span> {tr(a.text)}
                          </span>
                        </li>)}
                    </ul>
                  </Reveal>}

                <Reveal delay={0.35} className="mt-8 border-l-2 border-esaia-blue bg-white p-6">
                  <p className="text-sm leading-relaxed text-esaia-muted">{tr("The ERI is an introductory, non-binding readiness indication. It does not constitute an audit, certification or legal advice. A full ESAIA assessment examines your AI environment in depth and leads, where requirements are met, to certification valid for 12 months.")}{" "}</p>
                </Reveal>

                <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                  <Link to={lp(lang, "/contact?service=Individual%20Certification")} data-testid="eri-result-quote-button" className="group inline-flex items-center justify-center gap-2 bg-esaia-blue px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark">{tr("Discuss your result with ESAIA")}{" "}<ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                  <Link to={lp(lang, "/certification")} data-testid="eri-result-certification-button" className="inline-flex items-center justify-center gap-2 border border-esaia-ink/20 px-7 py-3.5 text-sm font-semibold text-esaia-ink transition-colors hover:bg-esaia-ink hover:text-white">{tr("Explore ESAIA Certification")}{" "}</Link>
                  <button onClick={downloadPdf} data-testid="eri-download-result-button" className="inline-flex items-center justify-center gap-2 border border-esaia-blue/40 px-7 py-3.5 text-sm font-semibold text-esaia-blue transition-colors hover:bg-esaia-blue hover:text-white">
                    <Download size={16} />{tr("Download my ERI result (PDF)")}{" "}</button>
                </div>
              </div>}

            {/* NAV BUTTONS */}
            {!onResult && <div className="mt-12 flex items-center justify-between border-t border-esaia-line pt-6">
                <button type="button" onClick={back} disabled={step === 0} data-testid="eri-back-button" className="inline-flex items-center gap-2 text-sm font-semibold text-esaia-muted transition-colors hover:text-esaia-ink disabled:opacity-0">
                  <ArrowLeft size={16} />{" "}{tr("Back")}{" "}</button>
                <button type="button" onClick={next} disabled={!stepValid() || submitting} data-testid={step === ERI_SECTIONS.length + 1 ? "eri-submit-button" : "eri-next-button"} className="group inline-flex items-center gap-2 bg-esaia-blue px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-esaia-dark disabled:cursor-not-allowed disabled:opacity-40">
                  {submitting ? tr("Calculating...") : step === ERI_SECTIONS.length + 1 ? tr("View my ERI result") : tr("Continue")}
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>}
          </motion.div>
        </AnimatePresence>
      </section>
    </div>;
}
